/**
 * usePrinting Hook - Printing interface for POS screen
 * Uses Local Print Server only (localhost:9632)
 * Supports "Print Last" even after app restart (fetches from backend API)
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { API } from '@/apiConfig';
import { printServerRequest, isPrintServerNetworkError } from '@/lib/printServer';

export function usePrinting(hardwareSettings) {
  const [isPrinting, setIsPrinting] = useState(false);
  const [isOpeningDrawer, setIsOpeningDrawer] = useState(false);

  // Store last transaction data for reprinting (in-memory for current session)
  const lastTransactionRef = useRef(null);

  // Warm up the print-server's persistent PowerShell worker as soon as this
  // hook mounts, so the first drawer kick / receipt is instant instead of
  // paying the 2-3s PowerShell cold-start cost.
  useEffect(() => {
    if (window.location.pathname.startsWith('/backoffice')) return; // no local printer to warm up in Back Office
    printServerRequest('post', '/api/warmup', {}).catch(() => { /* fine — worker warms lazily */ });
  }, []);

  const shouldPrintReceipt = useCallback((paymentMethod) => {
    if (!hardwareSettings?.receipt_printer_enabled) return false;
    const printMode = hardwareSettings?.receipt_print_mode || 'all';
    switch (printMode) {
      case 'all': return true;
      case 'card_only': return paymentMethod !== 'cash';
      case 'cash_only': return paymentMethod === 'cash';
      case 'none': return false;
      default: return true;
    }
  }, [hardwareSettings]);

  /**
   * Print receipt via Local Print Server (includes tenders for card/EBT/cash details)
   */
  const printViaLocalServer = useCallback(async (storeSettings, cartItems, totals, paymentMethod, tenders) => {
    await printServerRequest('post', '/api/print', {
      printerName: hardwareSettings.receipt_printer_name,
      storeSettings,
      cartItems,
      totals,
      paymentMethod,
      tenders: tenders || []
    }, { timeoutMs: 3000 });
    return true;
  }, [hardwareSettings]);

  /**
   * Print receipt for a completed transaction
   */
  const printReceipt = useCallback(async (storeSettings, cartItems, totals, paymentMethod, transactionNumber, tenders) => {
    if (!hardwareSettings?.receipt_printer_enabled || !hardwareSettings?.receipt_printer_name) {
      return false;
    }

    // Store for potential reprint
    lastTransactionRef.current = { storeSettings, cartItems, totals, paymentMethod, transactionNumber, tenders, timestamp: new Date() };

    setIsPrinting(true);
    try {
      await printViaLocalServer(storeSettings, cartItems, totals, paymentMethod, tenders);
      return true;
    } catch (error) {
      console.error('Print failed:', error);
      return false;
    } finally {
      setIsPrinting(false);
    }
  }, [hardwareSettings, printViaLocalServer]);

  /**
   * Print receipt after transaction
   * Note: Cash drawer is now opened immediately in PaymentDialog when change popup appears
   */
  const handleTransactionComplete = useCallback(async (storeSettings, cartItems, totals, paymentMethod, transactionNumber, tenders) => {
    const results = { printed: false, drawerOpened: false };

    lastTransactionRef.current = { storeSettings, cartItems, totals, paymentMethod, transactionNumber, tenders, timestamp: new Date() };

    if (shouldPrintReceipt(paymentMethod)) {
      results.printed = await printReceipt(storeSettings, cartItems, totals, paymentMethod, transactionNumber, tenders);
    }

    return results;
  }, [shouldPrintReceipt, printReceipt]);

  /**
   * Reprint last receipt — always fetches the latest transaction from backend
   * to guarantee it's the most recent one, then prints via thermal printer.
   */
  const printLastReceipt = useCallback(async () => {
    if (!hardwareSettings?.receipt_printer_enabled || !hardwareSettings?.receipt_printer_name) {
      return { success: false, error: 'Receipt printer not configured' };
    }

    setIsPrinting(true);
    try {
      const token = localStorage.getItem('token');
      const authHeaders = token ? { 'Authorization': `Bearer ${token}` } : {};

      // Fetch latest transaction and store settings in parallel
      const [txnRes, storeRes] = await Promise.all([
        fetch(`${API}/transactions?limit=1`, { headers: authHeaders }),
        fetch(`${API}/settings/store`, { headers: authHeaders })
      ]);

      if (!txnRes.ok) {
        return { success: false, error: `Backend error (${txnRes.status}) — try logging in again` };
      }

      const transactions = await txnRes.json();
      const lastTxn = Array.isArray(transactions) ? transactions[0] : null;
      if (!lastTxn) {
        return { success: false, error: 'No transactions found to reprint' };
      }

      const storeSettings = storeRes.ok ? await storeRes.json() : {};

      await printServerRequest('post', '/api/print/transaction', {
        printerName: hardwareSettings.receipt_printer_name,
        transaction: lastTxn,
        storeSettings
      }, { timeoutMs: 10000 });

      return {
        success: true,
        transactionNumber: lastTxn.transaction_number || lastTxn.id?.slice(-6)
      };
    } catch (error) {
      if (isPrintServerNetworkError(error)) {
        return { success: false, error: `${error.message} — the print server starts with Modesk POS on this PC (port 9632).` };
      }
      return { success: false, error: error.response?.data?.error || error.message || 'Unknown print error' };
    } finally {
      setIsPrinting(false);
    }
  }, [hardwareSettings]);

  /**
   * Check if there's a last transaction (always true now - we can fetch from backend)
   */
  const hasLastTransaction = useCallback(() => {
    return true; // Always allow - we'll fetch from backend if needed
  }, []);

  /**
   * Open cash drawer
   */
  const openCashDrawer = useCallback(async () => {
    if (!hardwareSettings?.cash_drawer_enabled) {
      return false;
    }
    const drawerPrinter = hardwareSettings?.cash_drawer_printer_name || hardwareSettings?.receipt_printer_name;
    if (!drawerPrinter) {
      return false;
    }
    setIsOpeningDrawer(true);
    try {
      await printServerRequest('post', '/api/drawer/open', { printerName: drawerPrinter }, { timeoutMs: 10000 });
      return true;
    } catch (error) {
      console.error('Cash drawer failed:', error.response?.data?.error || error.message);
      return false;
    } finally {
      setIsOpeningDrawer(false);
    }
  }, [hardwareSettings]);

  return {
    isPrinting,
    isOpeningDrawer,
    shouldPrintReceipt,
    printReceipt,
    handleTransactionComplete,
    printLastReceipt,
    hasLastTransaction,
    openCashDrawer
  };
}

export default usePrinting;
